/**
 * GA4 & Google Ads Analytics utility.
 * - Loads gtag.js dynamically only after cookie consent
 * - All track functions are safe to call without consent (no-op)
 * - Integrates both GA4 (G-P23PB1HKNE) and Google Ads (AW-18285721537)
 */

const GA_MEASUREMENT_ID = 'G-P23PB1HKNE';
const GOOGLE_ADS_ID = 'AW-18285721537'; // CPDForge AI Ltd tag (account 957-574-7660) — runs all 3 live campaigns
const PURCHASE_CONVERSION_SEND_TO = 'AW-18285721537/leJXCKDhyckcEMHvp49E'; // Purchase conversion action (Page load), same account as campaigns
const UET_TAG_ID = '97254688'; // Microsoft Advertising UET (Bing) — CPDForge AI Site UET

import { trackCommercial } from './attribution';

let initialized = false;
let uetInitialized = false;

// Buffer for events fired BEFORE cookie consent is granted. Nothing is sent to
// GA/Ads until initGA() runs (PECR-compliant), but events triggered on the first
// page (e.g. view_item on a direct ad landing) are replayed on Accept instead of
// being silently dropped. Discarded on Reject/Essential.
let _pendingEvents = [];
const _MAX_PENDING = 50;

// Read cookie-consent decision without importing CookieBanner (avoids a cycle).
// Mirrors CONSENT_KEY in components/CookieBanner.js.
const _consentDecision = () => {
  try { return localStorage.getItem('cookie_consent'); } catch (e) { return null; }
};

// ── Microsoft Advertising UET (consent-gated, Consent Mode) ─────────────────
/** Load the Bing UET tag — call only after cookie consent (Accept). */
export function initUET() {
  if (uetInitialized || typeof window === 'undefined') return;
  window.uetq = window.uetq || [];
  // Consent Mode: default denied until granted below (we only init after Accept).
  window.uetq.push('consent', 'default', { ad_storage: 'denied' });
  (function (w, d, t, u, o) {
    w[u] = w[u] || [];
    o.ts = (new Date()).getTime();
    const n = d.createElement(t);
    n.src = 'https://bat.bing.net/bat.js?ti=' + o.ti + ('uetq' !== u ? '&q=' + u : '');
    n.async = 1;
    n.onload = n.onreadystatechange = function () {
      const s = this.readyState;
      if (!s || s === 'loaded' || s === 'complete') {
        o.q = w[u];
        w[u] = new window.UET(o);
        w[u].push('pageLoad');
        n.onload = n.onreadystatechange = null;
      }
    };
    const i = d.getElementsByTagName(t)[0];
    i.parentNode.insertBefore(n, i);
  })(window, document, 'script', 'uetq', { ti: UET_TAG_ID, enableAutoSpaTracking: true });
  // Consent granted (this only runs after the user Accepts).
  window.uetq.push('consent', 'update', { ad_storage: 'granted' });
  uetInitialized = true;
}

/** Withdraw UET consent — call when consent is refused/withdrawn. */
export function disableUET() {
  if (typeof window !== 'undefined') {
    window.uetq = window.uetq || [];
    window.uetq.push('consent', 'update', { ad_storage: 'denied' });
  }
  uetInitialized = false;
}

/** Fire a Microsoft UET conversion/custom event — safe no-op until UET is loaded. */
export function uetEvent(action, params = {}) {
  try {
    if (typeof window !== 'undefined' && window.uetq) {
      window.uetq.push('event', action, params);
    }
  } catch (e) { /* never block the app */ }
}

// ── Enhanced Conversions (hashed-email, consent-gated) ──────────────────────
// Lifts measured conversions on both Google Ads & Microsoft Advertising by
// matching signed-in users to ad clicks via a SHA-256 hash of their email.
// No raw PII is ever sent or stored — only the hash. Consent-gated: identity is
// only attached once gtag / UET are initialised (i.e. after cookie Accept).
let _ecHashedEmail = null;

/** Normalise an email (Google/Microsoft rules) and return its SHA-256 hex hash. */
async function hashEmailSHA256(email) {
  try {
    if (!email || typeof email !== 'string') return null;
    if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle) return null;
    let e = email.trim().toLowerCase();
    if (e.indexOf('@') === -1) return null;
    let [local, domain] = e.split('@');
    local = local.replace(/\+.*$/, '');                       // strip +alias
    if (domain === 'gmail.com' || domain === 'googlemail.com') {
      local = local.replace(/\./g, '');                       // drop gmail dots
    }
    e = `${local}@${domain}`;
    const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(e));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (_) {
    return null;
  }
}

/**
 * Set the hashed-email identity for Enhanced Conversions on BOTH providers.
 * Call at login / signup / purchase where the email is known. Idempotent & safe.
 */
export async function setEnhancedConversionsIdentity(email) {
  const hashed = await hashEmailSHA256(email);
  if (!hashed) return null;
  _ecHashedEmail = hashed;
  // Google Ads / GA4 — user_data persists for subsequent conversion hits this session.
  if (initialized && typeof window.gtag === 'function') {
    window.gtag('set', 'user_data', { sha256_email_address: hashed });
  }
  // Microsoft UET — persistent set so subsequent conversion events carry the hash.
  if (uetInitialized && typeof window !== 'undefined' && window.uetq) {
    try { window.uetq.push('set', { pid: { em: hashed } }); } catch (_) { /* noop */ }
  }
  return hashed;
}

/** Enhanced-conversion pid block for UET events (empty until identity is set + consented). */
const _uetPid = () => (_ecHashedEmail ? { pid: { em: _ecHashedEmail } } : {});

/** Load GA4 + Google Ads script and initialize — call only after consent */
export function initGA() {
  if (initialized) return;

  // Load gtag.js script (shared for both GA4 and Google Ads)
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  
  // Configure GA4
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: true });
  
  // Configure Google Ads with conversion linker (this account holds both the
  // site/remarketing tag AND the Purchase conversion action + EC for web).
  window.gtag('config', GOOGLE_ADS_ID);

  initialized = true;

  // Flush any events that were triggered before consent (e.g. view_item on the
  // direct ad-landing page). These fire now that consent is granted.
  if (_pendingEvents.length) {
    const queued = _pendingEvents;
    _pendingEvents = [];
    queued.forEach(({ name, params }) => {
      try { window.gtag('event', name, params); } catch (e) { /* no-op */ }
    });
  }
}

/** Disable GA4 & Google Ads — call when consent is withdrawn */
export function disableGA() {
  if (typeof window !== 'undefined') {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    window[`ga-disable-${GOOGLE_ADS_ID}`] = true;
  }
  // Consent refused/withdrawn: discard anything buffered pre-consent.
  _pendingEvents = [];
  initialized = false;
}

/** Track custom event — safe to call without consent.
 *  Before consent: buffered (replayed on Accept). After consent: sent live. */
export function trackEvent(name, params = {}) {
  if (window.gtag && initialized) {
    window.gtag('event', name, params);
  } else if (_consentDecision() !== 'rejected' && _consentDecision() !== 'essential') {
    // Consent not yet decided — buffer so the event isn't lost on the landing page.
    if (_pendingEvents.length < _MAX_PENDING) {
      _pendingEvents.push({ name, params });
    }
  }
}

/** Track page view */
export function trackPageView(path) {
  if (window.gtag && initialized) {
    window.gtag('config', GA_MEASUREMENT_ID, { page_path: path });
  }
}

// Specific event helpers
export const trackSignup = (params = {}) => {
  // Normalize: accept string (legacy) or object
  const p = typeof params === 'string' ? { method: params } : params;
  const eventParams = {
    method: p.method || 'email',
    ...(p.plan_id && { plan_id: p.plan_id }),
    ...(p.plan_name && { plan_name: p.plan_name }),
    ...(p.plan_value != null && { plan_value: p.plan_value }),
    currency: 'GBP',
  };

  // Critical conversion: bypass consent-gate, fire directly if gtag exists
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'sign_up', eventParams);
  } else {
    // Fallback: push to dataLayer for GTM / deferred processing
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'sign_up', ...eventParams });
    } catch (e) { /* never block signup */ }
  }

  // Dev-only verification log
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] sign_up fired', eventParams);
  }
};
/**
 * CRITICAL CONVERSION EVENT — fires after successful account creation.
 * Bypasses consent gate (same as trial_cta_click / sign_up).
 * Fires exactly once per successful register() call.
 */
export const trackSignupComplete = (params = {}) => {
  const eventParams = {
    signup_source: params.signup_source || 'direct',
    page_path: window.location.pathname,
    method: params.method || 'email',
    ...(params.plan_id && { plan_id: params.plan_id }),
  };

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'signup_complete', eventParams);
  } else {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'signup_complete', ...eventParams });
    } catch (e) { /* never block signup */ }
  }

  // Mirror to Microsoft Advertising (UET) — conversion goal can match action 'signup'.
  uetEvent('signup', { event_category: 'account', event_label: eventParams.signup_source, ..._uetPid() });

  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] signup_complete fired', eventParams);
  }
};

export const trackCourseGeneration = (courseId) => trackEvent('course_generation', { course_id: courseId });
export const trackAIReview = (courseId) => trackEvent('ai_review_usage', { course_id: courseId });
export const trackUpgrade = (plan) => trackEvent('begin_checkout', { plan_name: plan });

// ═══════════════════════════════════════════════════════════════
// SPECIALIST PROGRAMME ECOMMERCE TRACKING (GA4 ecommerce schema)
// Data-driven: any published programme fires these with zero per-programme config.
// ═══════════════════════════════════════════════════════════════

/** Map a public-catalogue programme object → a GA4 ecommerce item */
const programmeToItem = (p = {}) => ({
  item_id: p.id,
  item_name: p.title,
  item_category: p.pillar || 'specialist_programme',
  item_variant: p.competency_display || p.role || undefined,
  price: p.price_cents ? p.price_cents / 100 : 0,
  quantity: 1,
});

const programmeCurrency = (p = {}) => (p.currency || 'GBP').toUpperCase();

/** Fire when a programme card is clicked anywhere (rails, catalogue, signposts) */
export const trackProgrammeCardClick = (p = {}, source = 'unknown') => {
  trackEvent('programme_card_click', {
    source,
    item_id: p.id,
    item_name: p.title,
    item_category: p.pillar || 'specialist_programme',
    price: p.price_cents ? p.price_cents / 100 : 0,
    currency: programmeCurrency(p),
  });
};

/** Fire on the public programme detail page load (GA4 view_item) */
export const trackProgrammeView = (p = {}) => {
  const item = programmeToItem(p);
  trackEvent('view_item', { currency: programmeCurrency(p), value: item.price, items: [item] });
  trackCommercial('programme_view', { pageCategory: 'programme', props: { title: p.title, slug: p.slug, price: item.price } });
};

/** Fire when the learner starts a programme checkout (GA4 begin_checkout) */
export const trackProgrammeBeginCheckout = (p = {}) => {
  const item = programmeToItem(p);
  trackEvent('begin_checkout', { currency: programmeCurrency(p), value: item.price, items: [item] });
  trackCommercial('checkout_started', { pageCategory: 'programme', props: { title: p.title, slug: p.slug, price: item.price } });
};

/** Phase 0 commercial-journey funnel — one-off credential purchase.
 *  programme_page (view_item) → modal_open → registration_started →
 *  registration_completed → begin_checkout/stripe_reached → purchase → programme_started */
export const trackProgrammePurchaseModalOpen = (p = {}) => {
  const item = programmeToItem(p);
  trackEvent('programme_purchase_modal_open', { item_id: p.id, item_name: p.title, value: item.price, currency: programmeCurrency(p) });
  trackCommercial('purchase_modal_open', { pageCategory: 'programme', props: { title: p.title, slug: p.slug, price: item.price } });
};

export const trackProgrammeRegistrationStarted = (p = {}, mode = 'register') => {
  trackEvent('programme_registration_started', { item_id: p.id, item_name: p.title, auth_mode: mode });
  trackCommercial('purchase_registration_started', { pageCategory: 'programme', props: { title: p.title, slug: p.slug, auth_mode: mode } });
};

export const trackProgrammeRegistrationCompleted = (p = {}, mode = 'register') => {
  trackEvent('programme_registration_completed', { item_id: p.id, item_name: p.title, auth_mode: mode });
  trackCommercial('purchase_registration_completed', { pageCategory: 'programme', props: { title: p.title, slug: p.slug, auth_mode: mode } });
};

export const trackProgrammeStripeReached = (p = {}) => {
  const item = programmeToItem(p);
  trackEvent('programme_stripe_reached', { item_id: p.id, item_name: p.title, value: item.price, currency: programmeCurrency(p) });
  trackCommercial('purchase_stripe_reached', { pageCategory: 'programme', props: { title: p.title, slug: p.slug, price: item.price } });
};

export const trackProgrammeStarted = (p = {}, source = 'purchase_success') => {
  trackEvent('programme_started', { item_id: p?.id, item_name: p?.title, source });
  trackCommercial('programme_started', { pageCategory: 'programme', props: { title: p?.title, source } });
};

/** Fire once a programme purchase is fulfilled (GA4 purchase). amountTotal in minor units. */
export const trackProgrammePurchase = ({ transactionId, programmeId, title, amountTotal, currency } = {}) => {
  const value = amountTotal != null ? amountTotal / 100 : 0;
  trackEvent('purchase', {
    transaction_id: transactionId,
    currency: (currency || 'GBP').toUpperCase(),
    value,
    items: [{ item_id: programmeId, item_name: title, item_category: 'specialist_programme', price: value, quantity: 1 }],
  });
  trackCommercial('purchase_completed', { pageCategory: 'programme', props: { title, programme_id: programmeId, value } });
  // Google Ads Purchase conversion (option b): fires against the conversion
  // account's tag with value + currency + transaction_id (for value-based
  // bidding and de-duplication). Consent-gated via `initialized`.
  if (initialized && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      send_to: PURCHASE_CONVERSION_SEND_TO,
      value,
      currency: (currency || 'GBP').toUpperCase(),
      transaction_id: transactionId || '',
    });
  }
  // Mirror to Microsoft Advertising (UET) with revenue — conversion goal matches action 'purchase'.
  uetEvent('purchase', {
    event_category: 'ecommerce',
    event_label: title,
    revenue_value: value,
    currency: (currency || 'GBP').toUpperCase(),
    transaction_id: transactionId,
    ..._uetPid(),
  });
};

/** Phase 1 — related/next-step programme click (ladder, related, alternatives) */
export const trackProgrammeRelatedClick = (p = {}, relation = 'related') => {
  trackEvent('programme_related_click', {
    relation,
    item_id: p.id,
    item_name: p.title,
    level_label: p.level_label || undefined,
  });
};

/** Phase 1 refinement (R3) — "Recommended next step" card shown (measures up-sell exposure).
 *  Pair with `purchase` (same item_id) in GA4 to quantify onward-credential lift. */
export const trackNextStepImpression = (p = {}, source = 'programme_detail') => {
  trackEvent('programme_next_step_impression', {
    source, item_id: p.id, item_name: p.title, level_label: p.level_label || undefined,
  });
};

/** Phase 1 refinement (R3) — "Recommended next step" card clicked. */
export const trackNextStepClick = (p = {}, source = 'programme_detail') => {
  trackEvent('programme_next_step_click', {
    source, item_id: p.id, item_name: p.title, level_label: p.level_label || undefined,
  });
};

/** Phase 1 — public verification / evidence-pack demo opened */
export const trackVerifyDemoClick = (source = 'programme_detail') => {
  trackEvent('verify_demo_click', { source, page_path: window.location.pathname });
};

/** Phase 1 — programme FAQ / explainer expanded */
export const trackProgrammeFaqOpen = (question = '', programmeId = '') => {
  trackEvent('programme_faq_open', { question: String(question).slice(0, 120), item_id: programmeId });
};

/** Phase 1 — sticky mobile CTA interaction */
export const trackStickyCtaClick = (p = {}) => {
  trackEvent('sticky_cta_click', { item_id: p.id, item_name: p.title, currency: (p.currency || 'GBP').toUpperCase() });
};

/** Phase 2a — credential-family hub viewed */
export const trackFamilyView = (family = {}) => {
  trackEvent('credential_family_view', {
    family_key: family.family_key, family_label: family.family_label,
    member_count: family.member_count, page_path: window.location.pathname,
  });
  trackCommercial('credential_family_view', { pageCategory: 'credential_family', props: { title: family.family_label, family_key: family.family_key } });
};

/** Phase 2a — a ladder rung / CTA on the family hub clicked through */
export const trackFamilyRungClick = (rung = {}, familyKey = '') => {
  trackEvent('credential_family_rung_click', {
    family_key: familyKey, item_id: rung.id, item_name: rung.title, level_label: rung.level_label,
  });
};


// Conversion funnel tracking

/** Fire when user opens the trial modal (funnel step, not conversion) */
export const trackTrialModalOpen = (source, planId) => {
  const params = { source, ...(planId && { plan_id: planId }) };
  trackEvent('trial_modal_open', params);
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] trial_modal_open fired', params);
  }
};

/**
 * CRITICAL CONVERSION EVENT — fires when "Create account" is clicked
 * inside the trial modal. Bypasses consent gate because this is the
 * primary Google Ads conversion action.
 */
export const trackTrialCTAClick = (planId) => {
  const params = {
    button_location: 'trial_modal',
    page_path: window.location.pathname,
    ...(planId && { plan_id: planId }),
  };

  // Bypass consent-gate: fire directly if gtag exists (critical conversion)
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'trial_cta_click', params);
  } else {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'trial_cta_click', ...params });
    } catch (e) { /* never block conversion */ }
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] trial_cta_click fired', params);
  }
};

export const trackTrialModalAction = (action, planId) => {
  const params = { action, ...(planId && { plan_id: planId }) };
  trackEvent('trial_modal_action', params);
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] trial_modal_action fired', params);
  }
};

// ═══════════════════════════════════════════════════════════════
// ASSESSMENT TOOL GA4 TRACKING
// ═══════════════════════════════════════════════════════════════

/**
 * Helper: Determine page type from pathname
 */
const getPageType = (pathname) => {
  if (pathname === '/') return 'homepage';
  if (pathname.startsWith('/compliance-training-assessment')) return 'landing';
  if (pathname.startsWith('/compare/')) return 'pillar';
  if (pathname.includes('-alternative')) return 'alternative';
  if (pathname === '/best-compliance-training-platforms') return 'hub';
  return 'other';
};

/**
 * 1. Track assessment landing page view
 */
export const trackAssessmentViewed = () => {
  const params = {
    page_location: window.location.href,
    page_referrer: document.referrer || 'direct',
  };
  
  trackEvent('assessment_viewed', params);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] assessment_viewed fired', params);
  }
};

/**
 * 2. Track assessment quiz start
 */
export const trackAssessmentStarted = (sourcePage, ctaLocation) => {
  const params = {
    source_page: sourcePage || window.location.pathname,
    cta_location: ctaLocation || 'unknown',
  };
  
  trackEvent('assessment_started', params);
  trackCommercial('assessment_started', { pageCategory: 'assessment', props: { source_page: params.source_page } });
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] assessment_started fired', params);
  }
};

/**
 * 3. Track individual question answered (LIGHTWEIGHT)
 */
export const trackQuestionAnswered = (questionId, questionNumber) => {
  const params = {
    question_id: questionId,
    question_number: questionNumber,
  };
  
  trackEvent('question_answered', params);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] question_answered fired', params);
  }
};

/**
 * 4. Track assessment completion
 */
export const trackAssessmentCompleted = (timeSeconds, questionsAnswered, optionalSkipped) => {
  const params = {
    total_time_seconds: timeSeconds,
    questions_answered: questionsAnswered,
    optional_skipped: optionalSkipped,
  };
  
  trackEvent('assessment_completed', params);
  trackCommercial('assessment_completed', { pageCategory: 'assessment', props: { questions_answered: questionsAnswered } });
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] assessment_completed fired', params);
  }
};

/**
 * 5. Track results page view (with duplicate protection via useRef in component)
 */
export const trackResultsViewed = (resultsData) => {
  const params = {
    session_id: resultsData.sessionId,
    top_1_platform: resultsData.top1Platform,
    top_1_score: resultsData.top1Score,
    top_1_fit_label: resultsData.top1FitLabel,
    top_3_platforms: resultsData.top3Platforms,
    cpdforge_rank: resultsData.cpdforgeRank,
    confidence_level: resultsData.confidenceLevel,
    primary_need: resultsData.primaryNeed,
    pillar_category: resultsData.pillarCategory,
  };
  
  trackEvent('results_viewed', params);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] results_viewed fired', params);
  }
};

/**
 * 6. Track CTA clicks across site
 */
export const trackAssessmentCTA = (ctaDetails) => {
  const params = {
    cta_text: ctaDetails.text,
    cta_type: ctaDetails.type,
    source_page: ctaDetails.sourcePage || window.location.pathname,
    destination: ctaDetails.destination,
    position: ctaDetails.position,
    page_type: ctaDetails.pageType || getPageType(window.location.pathname),
  };
  
  trackEvent('cta_clicked', params);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] cta_clicked fired', params);
  }
};

/**
 * 7. Track email capture
 */
export const trackEmailCapture = (emailData) => {
  const params = {
    session_id: emailData.sessionId,
    name_provided: !!emailData.name,
    organization_provided: !!emailData.organization,
    top_1_platform: emailData.top1Platform,
    email_success: emailData.success,
  };
  
  trackEvent('email_captured', params);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] email_captured fired', params);
  }
};

/**
 * V3 UX TRACKING - Decision-Led Assessment Flow
 */

/**
 * Track top nav "Find Your Platform" click
 */
export const trackTopNavAssessmentClick = () => {
  const params = {
    source_page: window.location.pathname,
  };
  
  trackEvent('top_nav_assessment_click', params);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] top_nav_assessment_click fired', params);
  }
};

/**
 * Track decision summary view on results page
 */
export const trackDecisionSummaryViewed = (summaryData) => {
  const params = {
    session_id: summaryData.sessionId,
    top_platform: summaryData.topPlatform,
    cpdforge_rank: summaryData.cpdforgeRank,
  };
  
  trackEvent('decision_summary_viewed', params);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] decision_summary_viewed fired', params);
  }
};

/**
 * Track feature request submission
 */
export const trackFeatureRequestSubmitted = (requestData) => {
  const params = {
    session_id: requestData.sessionId,
    top_platform: requestData.topPlatform,
    primary_need: requestData.primaryNeed,
    cpdforge_rank: requestData.cpdforgeRank,
  };
  
  trackEvent('feature_request_submitted', params);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] feature_request_submitted fired', params);
  }
};

/**
 * Track feature request email opt-in
 */
export const trackFeatureRequestNotify = (requestData) => {
  const params = {
    session_id: requestData.sessionId,
  };
  
  trackEvent('feature_request_notify_opt_in', params);
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[analytics] feature_request_notify_opt_in fired', params);
  }
};
